import { useCallback, useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FormProvider from "@/src/components/hook-form/FormProvider";
import {
    RHFAutocomplete,
    RHFCheckboxGroup,
    RHFTextField,
} from "@/src/components/hook-form";
import FileUpload from "@/src/components/upload";
import CustomTooltip from "@/src/lib/ui/customTooltip";
import { CustomButton, CustomIconButton } from "@/src/lib/ui";
import { GENDER } from "@/src/lib/module-helper";
import Iconify from "@/src/components/iconify";
import IconCode from "@/src/lib/iconCode";
import HodDesktopDatePicker from "@/src/lib/ui/hodDesktopDatePicker";
import { HISTORY_OPTIONS, HISTORY_DETAILS } from "@/src/lib/util/config";
import { useSelector } from "react-redux";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import { cartApiService } from "@/src/lib/api-services/cartApiServices";
import {
    updateCheckoutId,
    updateCurrentStep,
    appointmentSteps,
    updateMobileNo,
} from "@/src/lib/store/pet-ct-appointment-new/slice";
import { useDispatch } from "react-redux";
import { useToast } from "@/src/lib/hooks/useToast";
import Progressbar from "@/src/components/loading";
import PatientDropDown from "./PatientDropDown";
import { ConfirmDialog } from "@/src/lib/ui";
import { useCustomQuery } from "@/src/lib/hooks/useFetch";
import { testApiService } from "@/src/lib/api-services/testApiServices";
import CreateMemberForm from "../../my-account/members/add-member/createMemberForm";
import useAuth from "@/src/lib/hooks/useAuth";
import { formatDate } from "@/src/components/common/common";

const SelectPatient = () => {
    const appointment = useSelector((state) => state?.appointmentNew);
    const { isLoggedIn } = useAuth();
    const dispatch = useDispatch();
    const { showCustomToast } = useToast();
    const [uploadedFile, setUploadedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [open, setOpen] = useState(false);

    const {
        centreId,
        itemId,
        preferredDate,
        formattedTime,
        isSugarControlRequired,
        testPreparation,
    } = useMemo(() => {
        const cId = appointment?.centreDetails?.centreId;
        const iId = appointment?.testDetails?.itemId;
        const date = appointment?.selectedDateTime?.date;
        const time = appointment?.selectedDateTime?.slotTime;
        const isSugarControlRequired =
            appointment?.testDetails?.isSugarControlRequired;
        const testPreparation = appointment?.testDetails?.testPreparation;
        const timeFormatted = (time || "").split(".")[0] || null;
        return {
            centreId: cId,
            itemId: iId,
            preferredDate: date,
            formattedTime: timeFormatted,
            isSugarControlRequired: isSugarControlRequired,
            testPreparation: testPreparation,
        };
    }, [appointment]);

    const {
        data: patientList,
        isLoading,
        refetch: refetchPatientList,
    } = useCustomQuery("patient_list", testApiService.getPatientList, {
        enabled: true,
    });

    const patients = useMemo(
        () => patientList?.data?.data || [],
        [patientList]
    );

    const onClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const onAddPatient = useCallback(() => {
        setOpen(true);
    }, [setOpen]);

    useEffect(() => {
        if (isLoggedIn) {
            refetchPatientList();
        }
    }, [isLoggedIn]);

    const schema = useMemo(
        () =>
            Yup.object().shape({
                firstName: Yup.string()
                    .trim()
                    .min(2, "Minimum 2 characters required")
                    .required("First Name is required"),
                gender: Yup.object()
                    .nullable()
                    .required("Please select a gender"),
                mobile: Yup.string()
                    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile")
                    .required("Mobile is required"),
                email: Yup.string()
                    .email("Invalid email format")
                    .required("Email is required"),
                dob: Yup.mixed().required("Date of birth required"),
                referredBy: Yup.string()
                    .trim()
                    .min(4, "Minimum 4 characters required")
                    .required("Referred By is required"),
                // Optional fields
                confirmHistory: Yup.array().of(Yup.string()).nullable(),
                clinicalHistory: Yup.string().nullable(),
                docPrescription: Yup.mixed().nullable(),
            }),
        []
    );
    const defaultValues = useMemo(
        () => ({
            firstName: "",
            gender: GENDER?.[0] || null,
            mobile: "",
            email: "",
            dob: new Date("1980-01-01"),
            referredBy: "",
            confirmHistory: [],
            clinicalHistory: "",
            docPrescription: null,
        }),
        []
    );
    const methods = useForm({
        defaultValues,
        resolver: yupResolver(schema),
        mode: "onSubmit",
        shouldUnregister: true,
    });
    const { setValue } = methods;
    const { handleSubmit, control, watch } = methods;
    const onSubmit = (data) => {
        const confirmHistory = Object.fromEntries(
            HISTORY_OPTIONS.map((option) => [
                option.value,
                data?.confirmHistory?.includes(option.value) ? 1 : 0,
            ])
        );

        const dob = data.dob ? formatDate(data.dob, "YYYY-MM-DD") : null;
        const payload = {
            itemId: itemId,
            centreId: centreId,
            mobile: data.mobile.toString(),
            pname: data.firstName,
            dob: dob,
            isLogin: 0,
            pid: data.pid || 0,
            gender: data.gender.value,
            email: data.email,
            referredBy: data.referredBy,
            clinicalHistory: data.clinicalHistory,
            preferredDate: preferredDate,
            preferredTime: formattedTime,
            docPrescription: uploadedFile,
            ...confirmHistory,
        };

        mutate(payload);
    };
    const confirmHistory = watch("confirmHistory") || [];

    const handleFileChange = useCallback(
        (e) => {
            if (e.target.files?.length > 0) {
                const file = e.target.files[0];
                setValue("docPrescription", file);
                setUploadedFile(file);
                setFilePreviewUrl(URL.createObjectURL(file));
            }
        },
        [setValue]
    );
    const handleRemoveFile = useCallback(() => {
        setUploadedFile(null);
        setFilePreviewUrl(null);
    }, []);

    const isGenderEqual = useCallback(
        (option, value) => option?.value === value?.value,
        []
    );

    const { mutate, isLoading: isCreatingCheckout } = useHODMutation(
        (data) => cartApiService.getCreateCheckout(data),
        {
            onSuccess: (data, variables) => {
                dispatch(updateCheckoutId(data?.data?.checkoutId));
                dispatch(updateMobileNo(variables?.mobile || null));

                if (variables?.docPrescription) {
                    const formData = new FormData();
                    formData.append("checkoutId", data?.data?.checkoutId);
                    formData.append("file", variables?.docPrescription);
                    uploadPetctDocMutate(formData);
                } else {
                    dispatch(
                        updateCurrentStep(appointmentSteps.PAYMENT_CONFIRMATION)
                    );
                }
            },
            onError: (error) => {
                showCustomToast(
                    error?.response?.data?.message || error?.message,
                    "error"
                );
            },
        }
    );

    const { mutate: uploadPetctDocMutate, isLoading: isUploadingPetctDoc } =
        useHODMutation((data) => cartApiService.uploadPetctDoc(data), {
            onSuccess: (data) => {
                dispatch(
                    updateCurrentStep(appointmentSteps.PAYMENT_CONFIRMATION)
                );
                // setIsStep((prev) => prev + 1);
            },
            onError: (error) => {
                showCustomToast(
                    error?.response?.data?.message || error?.message,
                    "error"
                );
                dispatch(
                    updateCurrentStep(appointmentSteps.PAYMENT_CONFIRMATION)
                );
                // setIsStep((prev) => prev + 1);
            },
        });

    return (
        <>
            <p className="text-xs uppercase tracking-widest text-black font-medium md:mt-10 mt-4 md:mb-2 mb-1">
                Your Information
            </p>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {isLoggedIn && (
                    <PatientDropDown
                        onAddPatient={onAddPatient}
                        patients={patients}
                    />
                )}

                <div className="md:p-12 p-4 bg-white shadow-md">
                    {/* {shouldShowForm && ( */}
                    <div className="grid grid-cols-12 md:gap-6 gap-3">
                        <div className="col-span-12 md:col-span-6">
                            {/* <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Name
                            </p> */}
                            <RHFTextField
                                name="firstName"
                                label="Enter Name"
                                size="small"
                            />
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            {/* <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Gender
                            </p> */}
                            <RHFAutocomplete
                                label="Select Gender"
                                name="gender"
                                options={GENDER}
                                size="small"
                                disableClearable
                                getOptionLabel={(option) => option.label || ""}
                                isOptionEqualToValue={isGenderEqual}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            {/* <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Mobile
                            </p> */}
                            <RHFTextField
                                name="mobile"
                                label="Mobile"
                                size="small"
                            />
                        </div>

                        <div className="col-span-12 md:col-span-6">
                            {/* <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Email
                            </p> */}
                            <RHFTextField
                                name="email"
                                label="Email"
                                size="small"
                            />
                        </div>

                        <div className="col-span-12 md:col-span-6">
                            <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Date of Birth
                            </p>
                            <Controller
                                name="dob"
                                control={control}
                                rules={{
                                    required: "Date of birth required",
                                }}
                                render={({ field, fieldState }) => (
                                    <HodDesktopDatePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        disableFuture={true}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>

                        {/* <div className="col-span-12">
                            <p className="inline-block bg-primary/10 px-3 md:py-2 py-1 rounded-md font-semibold py-1 text-primary uppercase md:tracking-widest tracking-wider text-xs">
                               
                            </p>
                        </div> */}

                        <div className="col-span-12">
                            <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Referred By (Dr. Name)
                            </p>
                            <RHFTextField
                                name="referredBy"
                                size="small"
                                startAdornment="Dr."
                            />
                            {/* <p>Enter Self if Dr. Prescription is not available.</p> */}
                        </div>

                        <div className="col-span-12">
                            <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                History of the patient
                            </p>
                            <RHFCheckboxGroup
                                name="confirmHistory"
                                options={HISTORY_OPTIONS}
                            />
                        </div>

                        {confirmHistory.length > 0 && (
                            <div className="col-span-12">
                                <div className="flex flex-col space-y-3">
                                    {confirmHistory.map((historyKey) => (
                                        <div
                                            key={historyKey}
                                            className="text-xs text-primary rounded bg-primary/10 px-3 py-2 border border-primary/15"
                                        >
                                            <strong>
                                                {
                                                    HISTORY_OPTIONS.find(
                                                        (option) =>
                                                            option.value ===
                                                            historyKey
                                                    )?.label
                                                }
                                            </strong>
                                            {historyKey === "isDiabitic"
                                                ? isSugarControlRequired === 1
                                                    ? ` : ${HISTORY_DETAILS.isDiabitic}`
                                                    : ` : ${HISTORY_DETAILS.isDiabeticNonFDG}`
                                                : HISTORY_DETAILS[historyKey]
                                                ? ` : ${HISTORY_DETAILS[historyKey]}`
                                                : ""}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="col-span-12">
                            <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Test Preparation
                            </p>
                            <div
                                className="text-xs text-primary [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_li]:text-primary border border-primary/40 p-2 rounded-md bg-primary/5"
                                dangerouslySetInnerHTML={{
                                    __html: testPreparation,
                                }}
                            ></div>
                        </div>

                        <div className="col-span-12">
                            <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Upload Prescription
                            </p>
                            {uploadedFile ? (
                                <div className="flex flex-row items-center gap-1 justify-between bg-gray-100 p-1 rounded-md">
                                    <CustomTooltip title="Preview File">
                                        <p
                                            className="cursor-pointer font-medium md:text-sm text-xs"
                                            onClick={() => {
                                                window.open(
                                                    filePreviewUrl,
                                                    "_blank"
                                                );
                                            }}
                                        >
                                            {uploadedFile.name}
                                        </p>
                                    </CustomTooltip>
                                    <CustomTooltip title="Remove File">
                                        <CustomIconButton
                                            onClick={handleRemoveFile}
                                            className="cursor-pointer text-red-500 p-2"
                                        >
                                            <Iconify icon={IconCode.delete} />
                                        </CustomIconButton>
                                    </CustomTooltip>
                                </div>
                            ) : (
                                <div class="flex items-center">
                                    <label
                                        for="file-upload"
                                        class="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-2.5 shadow-md transition-all hover:border-primary/50 hover:bg-primary/10 hover:shadow-lg"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            class="h-6 w-6 text-primary/60"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M12 4.5v15m0-15 6 6m-6-6-6 6"
                                            />
                                        </svg>

                                        <span class="text-base font-medium text-gray-700">
                                            Upload File
                                        </span>
                                    </label>

                                    <input
                                        id="file-upload"
                                        type="file"
                                        class="hidden"
                                        onChange={handleFileChange}
                                    />
                                </div>

                                // <FileUpload
                                //     buttonContent={
                                //         <div className="flex flex-col items-center justify-center h-40">
                                //             <p className="font-bold text-black capitalize text-lg">
                                //                 Upload Prescription
                                //             </p>
                                //             {/* <Iconify
                                //                 width={52}
                                //                 icon={IconCode.upload}
                                //                 className="text-primary mb-1"
                                //             />
                                //             <p className="font-bold text-black capitalize text-lg">
                                //                 Click here to upload file
                                //             </p>
                                //             <p className="text-gray-500 text-sm normal-case">
                                //                 Accepted formats: .jpg, .jpeg,
                                //                 .png
                                //             </p> */}
                                //         </div>
                                //     }
                                //     onChange={handleFileChange}
                                //     customButtonStyles="w-full bg-white border border-primary border-dashed shadow-none min-h-40"
                                // />
                            )}
                        </div>
                        <div className="col-span-12">
                            <p className="block font-semibold py-1 text-gray-700 uppercase tracking-widest text-xs">
                                Clinical History
                            </p>
                            <RHFTextField
                                name="clinicalHistory"
                                label="Clinical History"
                                size="small"
                                multiline
                                rows={4}
                            />
                        </div>

                        <div className="col-span-12">
                            <CustomButton
                                disabled={
                                    isCreatingCheckout || isUploadingPetctDoc
                                }
                                type="submit"
                                size="large"
                                fullWidth
                                variant="contained"
                                className="md:py-3 py-2 hover:bg-primary hover:text-white"
                            >
                                {isCreatingCheckout || isUploadingPetctDoc ? (
                                    <Progressbar size={24} />
                                ) : (
                                    "Next"
                                )}
                            </CustomButton>
                        </div>
                    </div>
                </div>
            </FormProvider>

            {open && (
                <ConfirmDialog
                    maxWidth="2xl"
                    open={open}
                    onClose={onClose}
                    isCancel={true}
                    title="Add Patient"
                    content={
                        <CreateMemberForm
                            noRedirect={true}
                            handleClose={onClose}
                            refetchPatientList={refetchPatientList}
                        />
                    }
                />
            )}
        </>
    );
};

export default SelectPatient;
